import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  screentimeColourField,
  screentimeDetailsField,
  screentimeMessageField,
  screentimeTitleField,
  UrlDisabled,
} from '@/features/channels/channelsSlice';
import {
  normalizeScreentimeColour,
  screentimeColourOrDefault,
} from '@/features/screentime/screenTimeApi';

import Button from '@/components/Common/Button';
import ColourPicker, { colourNameKey } from '@/components/Common/ColourPicker';
import FlexRow from '@/components/Common/FlexRow';
import Input, { TextArea } from '@/components/Common/Input';
import SectionRule from '@/components/Common/SectionRule';

export interface BlockedScreenSettingsProps {
  // Own values for THIS scope — "" where the scope sets nothing.
  details:				string;
  colour:				string;
  title:				string;
  message:				string;
  // Project values, for the room-level placeholders and swatch fallback.
  projColour:			string;
  projTitle:			string;
  isRoom:				boolean;
  isLoaded?:			boolean;
  isReadOnly?:			boolean;
  updateField:			(field: string, val: string) => any;
}

const Hint = (props: {children?: React.ReactNode}) => (
  <span className="text-[--Dark-Grey] text-[12px] leading-[16px]">
    { props.children }
  </span>
);

interface SegmentedOption {
  value:				string;
  caption:				string;
  icon:					string;
}

// Two or three mutually exclusive choices, each worth an icon. A dropdown would
// hide the alternative behind a click; here both outcomes are on screen at once,
// which is what makes "this one paints no text at all" discoverable.
const Segmented = (props: {
  value:				string;
  options:				SegmentedOption[];
  isReadOnly?:			boolean;
  setValue:				(val: string) => any;
}) => (
  <div className="flex w-full items-stretch rounded-[4px] border-solid border-1 border-[--Mid-Grey] overflow-hidden">
  {props.options.map((opt: SegmentedOption, i: number) => {
    const selected = opt.value == props.value;

    return (
    <FlexRow key={opt.value}
             direction="column"
             justify="center"
             align="center"
             className={[
               'gap-[4px] px-[12px] py-[10px] select-none border-solid border-0 border-[--Mid-Grey]',
               i > 0 ? 'border-l-1' : '',
               props.isReadOnly ? 'cursor-default' : 'cursor-pointer',
               selected
               ?	'bg-[--primetime-30] border-b-2 border-b-[--Primetime-main]'
               :	'bg-white',
             ].join(' ')}
             onClick={() => {
               if (!props.isReadOnly)
                 props.setValue(opt.value);
             }}>
      <span className={[
              'material-icons text-[20px]',
              selected ? '!text-[--Primetime-main]' : '!text-[--Dark-Grey]',
            ].join(' ')}>
        { opt.icon }
      </span>
      <span className={[
              'text-[13px] text-center leading-[16px]',
              selected ? '!text-[--Primetime-main] font-medium' : '!text-black',
            ].join(' ')}>
        { opt.caption }
      </span>
    </FlexRow>
    );
  })}
  </div>
);

// The blocked screen's APPEARANCE — never whether it appears. All four keys are
// pure pass-through: the middleware stores them, resolves project -> room and
// hands them to the TV, which owns every built-in default and does the drawing.
//
// The one trap: a native colour control is never visually empty, so "unset" has
// to be tracked apart from whatever the swatch happens to be rendering. We show
// the inherited colour and write nothing until the manager actually picks one —
// echoing the control's value back on mount would save a colour nobody chose
// and quietly detach the room from its project default.
const BlockedScreenSettings = (props: BlockedScreenSettingsProps) => {
  const { t } = useTranslation();

  const readOnly = props.isReadOnly === true;

  const detailOpts: SegmentedOption[] = [
    // A room inherits this like every other screen time key, so at room level
    // the control needs the third state. At project level there is nothing to
    // inherit FROM and the choice is a plain either/or.
    ...(props.isRoom
        ?	[{
              value:	"",
              caption:	t('projects:project:screentime:details:project_default'),
              icon:		"settings_backup_restore",
            }]
        :	[]
       ),
    {
      value:	"1",
      caption:	t('projects:project:screentime:details:show'),
      icon:		"wysiwyg",
    },
    {
      value:	"0",
      caption:	t('projects:project:screentime:details:bare'),
      icon:		"desktop_windows",
    },
  ];

  // EMPTY means "show the panel" on the wire, so at project level an unset
  // field selects the Show segment rather than leaving nothing lit.
  const detailValue =	props.isRoom
						?	props.details
						:	(props.details || "1");

  // What the picker paints while this scope's own field is empty: the project's
  // colour at room level, the built-in blue otherwise. Display only — nothing
  // here is ever written back on its own.
  const shownColour =	normalizeScreentimeColour(props.colour) ||
						screentimeColourOrDefault(props.isRoom ? props.projColour : "");

  const hasOwnColour = normalizeScreentimeColour(props.colour) != '';

  const titlePlaceholder =	props.isRoom &&
							props.projTitle != '' &&
							props.projTitle != UrlDisabled
							?	props.projTitle
							:	t('projects:project:screentime:title:placeholder');

  return (
  <FlexRow direction="column"
           align="start"
           className="w-full max-w-[672px] gap-[0px] rounded-[8px] border-solid border-1 border-[--Lines] bg-white overflow-hidden">
    <FlexRow align="center"
             className="gap-[12px] px-[24px] py-[16px] border-solid border-0 border-b-1 border-[--Lines]">
      <FlexRow justify="center"
               align="center"
               width="w-[44px]"
               className="shrink-0 h-[44px] rounded-[8px] bg-[--primetime-30]">
        <span className="material-icons !text-[--Primetime-main] text-[22px]">
          tv
        </span>
      </FlexRow>
      <FlexRow direction="column"
               align="start"
               className="gap-[2px]">
        <span className="b14 !text-black">
          { t('projects:project:screentime:blocked_screen:caption') }
        </span>
        <Hint>
          { t('projects:project:screentime:blocked_screen:subtitle') }
        </Hint>
      </FlexRow>
    </FlexRow>

    <FlexRow direction="column"
             align="start"
             className="gap-[12px] px-[24px] pt-[8px] pb-[24px]">
      <SectionRule className="pt-[8px]" caption={t('projects:project:screentime:details:caption')} />
      <Segmented value={detailValue}
                 options={detailOpts}
                 isReadOnly={readOnly}
                 setValue={(val: string) => props.updateField(screentimeDetailsField, val)} />
      <Hint>
        { t('projects:project:screentime:details:help') }
      </Hint>

      <SectionRule className="pt-[8px]" caption={t('projects:project:screentime:colour:caption')} />
      <ColourPicker value={shownColour}
                    isReadOnly={readOnly}
                    name={t('common:colours:' + colourNameKey(shownColour))}
                    hexLabel={t('projects:project:screentime:colour:hex')}
                    redLabel={t('projects:project:screentime:colour:r')}
                    greenLabel={t('projects:project:screentime:colour:g')}
                    blueLabel={t('projects:project:screentime:colour:b')}
                    brightnessLabel={t('projects:project:screentime:colour:brightness')}
                    onChange={(hex: string) => props.updateField(screentimeColourField, hex)} />
      <Hint>
        { t('projects:project:screentime:colour:help') }
      </Hint>
    {/* Getting back to "unset" has to be its own action. The wheel cannot
        express it — every position on it is some colour — so without this the
        default is a one-way door. */}
    {!readOnly && hasOwnColour &&
      <Button caption={t('projects:project:screentime:colour:use_default')}
              icon="format_color_reset"
              layout="white"
              onClick={() => props.updateField(screentimeColourField, "")} />
    }

      <SectionRule className="pt-[8px]" caption={t('projects:project:screentime:text:caption')} />
      <FlexRow direction="column"
               align="start"
               className="gap-[4px]">
        <Input label={t('projects:project:screentime:title:caption')}
               isLoaded={props.isLoaded}
               isReadOnly={readOnly}
               placeholder={titlePlaceholder}
               value={props.title}
               onValueChange={(val: string) => props.updateField(screentimeTitleField, val)} />
        <Hint>
          { t('projects:project:screentime:title:help') }
        </Hint>
      </FlexRow>

      <FlexRow direction="column"
               align="start"
               className="gap-[4px]">
        <TextArea label={t('projects:project:screentime:message:caption')}
                  isLoaded={props.isLoaded}
                  isReadOnly={readOnly}
                  placeholder={t('projects:project:screentime:message:placeholder')}
                  value={props.message}
                  showChars={false}
                  onValueChange={(val: string) => props.updateField(screentimeMessageField, val)} />
        <Hint>
          { t('projects:project:screentime:message:help') }
        </Hint>
      </FlexRow>
    </FlexRow>
  </FlexRow>
  );
};

export default BlockedScreenSettings;
